/**
 * Currency utility functions for formatting monetary values
 */

export type CurrencyCode = 'INR' | 'USD' | 'EUR' | 'GBP' | 'AED';

interface CurrencyConfig {
  symbol: string;
  code: string;
  name: string;
  locale: string;
}

export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee', locale: 'en-IN' },
  USD: { symbol: '$', code: 'USD', name: 'US Dollar', locale: 'en-US' },
  EUR: { symbol: '€', code: 'EUR', name: 'Euro', locale: 'de-DE' },
  GBP: { symbol: '£', code: 'GBP', name: 'British Pound', locale: 'en-GB' },
  AED: { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham', locale: 'ar-AE' },
};

// Default currency for the platform (base currency for all stored amounts)
export const DEFAULT_CURRENCY: CurrencyCode = 'INR';

// Base currency for all stored amounts in database
export const BASE_CURRENCY: CurrencyCode = 'INR';

// localStorage key for cached exchange rates
const EXCHANGE_RATES_CACHE_KEY = 'exchangeRates';
const EXCHANGE_RATES_TIMESTAMP_KEY = 'exchangeRatesTimestamp';
const CACHE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Default/fallback exchange rates relative to INR (base currency)
 * Used when API is unavailable or for initial load
 * Rates represent: 1 INR = X foreign currency
 */
const DEFAULT_EXCHANGE_RATES: Record<CurrencyCode, number> = {
  INR: 1,           // Base currency
  USD: 0.012,       // 1 INR ≈ 0.012 USD (1 USD ≈ 83 INR)
  EUR: 0.011,       // 1 INR ≈ 0.011 EUR (1 EUR ≈ 91 INR)
  GBP: 0.0095,      // 1 INR ≈ 0.0095 GBP (1 GBP ≈ 105 INR)
  AED: 0.044,       // 1 INR ≈ 0.044 AED (1 AED ≈ 23 INR)
};

// Current active exchange rates (starts with defaults, updated from API)
let EXCHANGE_RATES: Record<CurrencyCode, number> = { ...DEFAULT_EXCHANGE_RATES };

/**
 * Get the current exchange rates
 */
export function getExchangeRates(): Record<CurrencyCode, number> {
  return { ...EXCHANGE_RATES };
}

/**
 * Load cached exchange rates from localStorage
 */
function loadCachedRates(): boolean {
  try {
    const cachedRates = localStorage.getItem(EXCHANGE_RATES_CACHE_KEY);
    const cachedTimestamp = localStorage.getItem(EXCHANGE_RATES_TIMESTAMP_KEY);
    
    if (cachedRates && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10);
      const now = Date.now();
      
      // Check if cache is still valid (less than 6 hours old)
      if (now - timestamp < CACHE_DURATION_MS) {
        EXCHANGE_RATES = JSON.parse(cachedRates);
        console.log('📊 Loaded cached exchange rates');
        return true;
      }
    }
  } catch (error) {
    console.error('Error loading cached rates:', error);
  }
  return false;
}

/**
 * Save exchange rates to localStorage cache
 */
function saveCachedRates(rates: Record<CurrencyCode, number>): void {
  try {
    localStorage.setItem(EXCHANGE_RATES_CACHE_KEY, JSON.stringify(rates));
    localStorage.setItem(EXCHANGE_RATES_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error caching rates:', error);
  }
}

/**
 * Fetch live exchange rates from a free API
 * Uses exchangerate-api.com (free tier: 1500 requests/month)
 * You can also use: api.frankfurter.app (free, no key needed)
 */
export async function fetchLiveExchangeRates(): Promise<boolean> {
  try {
    // Try to load from cache first
    if (loadCachedRates()) {
      // Still fetch in background to update cache
      fetchAndUpdateRates();
      return true;
    }
    
    return await fetchAndUpdateRates();
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    return false;
  }
}

/**
 * Internal function to fetch and update rates
 */
async function fetchAndUpdateRates(): Promise<boolean> {
  try {
    // Using frankfurter.app - free, no API key needed
    const response = await fetch(
      `https://api.frankfurter.app/latest?from=INR&to=USD,EUR,GBP,AED`
    );
    
    if (!response.ok) {
      throw new Error(`API responded with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.rates) {
      const newRates: Record<CurrencyCode, number> = {
        INR: 1,
        USD: data.rates.USD || DEFAULT_EXCHANGE_RATES.USD,
        EUR: data.rates.EUR || DEFAULT_EXCHANGE_RATES.EUR,
        GBP: data.rates.GBP || DEFAULT_EXCHANGE_RATES.GBP,
        AED: data.rates.AED || DEFAULT_EXCHANGE_RATES.AED,
      };
      
      EXCHANGE_RATES = newRates;
      saveCachedRates(newRates);
      
      console.log('✅ Exchange rates updated from API:', newRates);
      
      // Dispatch event so components can react to rate changes
      window.dispatchEvent(new CustomEvent('exchangeRatesUpdated', { detail: newRates }));
      
      return true;
    }
  } catch (error) {
    console.warn('⚠️ Could not fetch live rates, using cached/default rates:', error);
  }
  return false;
}

/**
 * Get the timestamp of when rates were last updated
 */
export function getRatesLastUpdated(): Date | null {
  try {
    const timestamp = localStorage.getItem(EXCHANGE_RATES_TIMESTAMP_KEY);
    if (timestamp) {
      return new Date(parseInt(timestamp, 10));
    }
  } catch (error) {
    console.error('Error getting rates timestamp:', error);
  }
  return null;
}

// Initialize: Load cached rates on module load
loadCachedRates();

/**
 * Convert amount from base currency (INR) to target currency
 * @param amountInINR - Amount in INR (base currency)
 * @param targetCurrency - Currency to convert to
 * @returns Converted amount
 */
export function convertFromBaseCurrency(
  amountInINR: number,
  targetCurrency: CurrencyCode | string
): number {
  const rate = EXCHANGE_RATES[targetCurrency as CurrencyCode] || 1;
  return amountInINR * rate;
}

/**
 * Convert amount from any currency to base currency (INR)
 * @param amount - Amount in source currency
 * @param sourceCurrency - Source currency code
 * @returns Amount in INR
 */
export function convertToBaseCurrency(
  amount: number,
  sourceCurrency: CurrencyCode | string
): number {
  const rate = EXCHANGE_RATES[sourceCurrency as CurrencyCode] || 1;
  return amount / rate;
}

/**
 * Convert amount between any two currencies
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency
 * @param toCurrency - Target currency
 * @returns Converted amount
 */
export function convertCurrency(
  amount: number,
  fromCurrency: CurrencyCode | string,
  toCurrency: CurrencyCode | string
): number {
  if (fromCurrency === toCurrency) return amount;
  
  // Convert to base currency (INR) first, then to target
  const amountInINR = convertToBaseCurrency(amount, fromCurrency);
  return convertFromBaseCurrency(amountInINR, toCurrency);
}

/**
 * Get currency symbol for a currency code
 */
export function getCurrencySymbol(currencyCode: CurrencyCode | string = DEFAULT_CURRENCY): string {
  const currency = CURRENCIES[currencyCode as CurrencyCode];
  return currency?.symbol || currencyCode;
}

/**
 * Format a number as currency
 */
export function formatCurrency(
  amount: number,
  currencyCode: CurrencyCode | string = DEFAULT_CURRENCY,
  options?: {
    showSymbol?: boolean;
    decimals?: number;
    compact?: boolean;
  }
): string {
  const { showSymbol = true, decimals = 2, compact = false } = options || {};
  const currency = CURRENCIES[currencyCode as CurrencyCode] || CURRENCIES[DEFAULT_CURRENCY];
  
  try {
    const formatted = new Intl.NumberFormat(currency.locale, {
      style: showSymbol ? 'currency' : 'decimal',
      currency: currency.code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      notation: compact ? 'compact' : 'standard',
    }).format(amount);
    
    return formatted;
  } catch {
    // Fallback formatting
    const symbol = showSymbol ? currency.symbol : '';
    const formattedAmount = amount.toLocaleString(currency.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${symbol}${formattedAmount}`;
  }
}

/**
 * Format and convert currency from base currency (INR) to display currency
 * This is the main function to use when displaying money values from database
 * @param amountInINR - Amount stored in database (in INR)
 * @param displayCurrency - User's preferred display currency
 * @param options - Formatting options
 * @returns Formatted string with converted amount
 */
export function formatDisplayCurrency(
  amountInINR: number,
  displayCurrency: CurrencyCode | string = DEFAULT_CURRENCY,
  options?: {
    showSymbol?: boolean;
    decimals?: number;
    compact?: boolean;
  }
): string {
  // Convert from INR to display currency
  const convertedAmount = convertFromBaseCurrency(amountInINR, displayCurrency);
  // Format with the target currency
  return formatCurrency(convertedAmount, displayCurrency, options);
}

/**
 * Format a range of currency values
 */
export function formatCurrencyRange(
  minAmount: number,
  maxAmount: number,
  currencyCode: CurrencyCode | string = DEFAULT_CURRENCY,
  options?: {
    decimals?: number;
    compact?: boolean;
  }
): string {
  const { decimals = 2, compact = false } = options || {};
  const symbol = getCurrencySymbol(currencyCode);
  
  const formatOptions = { showSymbol: false, decimals, compact };
  const min = formatCurrency(minAmount, currencyCode, formatOptions);
  const max = formatCurrency(maxAmount, currencyCode, formatOptions);
  
  return `${symbol}${min} - ${symbol}${max}`;
}

/**
 * Format a range of currency values with conversion from base currency (INR)
 * @param minAmountInINR - Minimum amount in INR
 * @param maxAmountInINR - Maximum amount in INR
 * @param displayCurrency - Target display currency
 * @param options - Formatting options
 */
export function formatDisplayCurrencyRange(
  minAmountInINR: number,
  maxAmountInINR: number,
  displayCurrency: CurrencyCode | string = DEFAULT_CURRENCY,
  options?: {
    decimals?: number;
    compact?: boolean;
  }
): string {
  const convertedMin = convertFromBaseCurrency(minAmountInINR, displayCurrency);
  const convertedMax = convertFromBaseCurrency(maxAmountInINR, displayCurrency);
  return formatCurrencyRange(convertedMin, convertedMax, displayCurrency, options);
}

/**
 * Parse a currency string to number
 */
export function parseCurrency(value: string): number {
  // Remove all non-numeric characters except decimal point and minus
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Get list of available currencies for dropdowns
 */
export function getCurrencyOptions(): { value: CurrencyCode; label: string }[] {
  return Object.entries(CURRENCIES).map(([code, config]) => ({
    value: code as CurrencyCode,
    label: `${config.symbol} ${config.code} - ${config.name}`,
  }));
}
